import type { AdminStaffProfile, GoogleWorkspacePermissionState } from '@oracord/shared';

export type StaffSetupRecoveryStatus =
  | 'unverified-google-email'
  | 'personal-google-email-blocked'
  | 'admin-setup-needed'
  | 'email-already-linked'
  | 'google-account-required'
  | 'google-workspace-sign-in-disabled'
  | 'unknown';

export interface StaffSetupRecoveryMessage {
  title: string;
  message: string;
  nextAction: string;
}

export function staffSetupRecoveryMessage(status: StaffSetupRecoveryStatus = 'unknown'): StaffSetupRecoveryMessage {
  switch (status) {
    case 'google-account-required':
      return {
        title: 'Continue with your work Google account',
        message: 'Sign in with the company Google Workspace account tied to your Team profile.',
        nextAction: 'Switch Google accounts, then try staff setup again.',
      };
    case 'google-workspace-sign-in-disabled':
      return {
        title: 'Google sign-in is not enabled yet',
        message: 'This workspace is not ready for Google Workspace staff setup.',
        nextAction: 'Ask an admin to enable Google Workspace sign-in before retrying.',
      };
    case 'unverified-google-email':
      return {
        title: 'Verify your work Google email',
        message: 'The Google account email must be verified before Oracord can match it.',
        nextAction: 'Verify the email in Google, then retry staff setup.',
      };
    case 'personal-google-email-blocked':
      return {
        title: 'Use your company Google account',
        message: 'Personal Gmail accounts cannot be matched to company Team profiles.',
        nextAction: 'Switch to your company Google Workspace account, then retry.',
      };
    case 'admin-setup-needed':
      return {
        title: 'No matching Team profile yet',
        message: 'Oracord could not find an active Team profile for this verified work email.',
        nextAction: 'Ask an admin to add your work email to your Team profile, then retry.',
      };
    case 'email-already-linked':
      return {
        title: 'This email is already linked',
        message: 'A Team profile is already connected to this work email or Google account.',
        nextAction: 'Ask an admin to confirm which Team profile should be connected. Do not create a second staff-entered number.',
      };
    default:
      return {
        title: 'Setup needs attention',
        message: 'Oracord could not confirm the staff setup state for this account.',
        nextAction: 'Refresh, then ask an admin to check your Team profile if this keeps happening.',
      };
  }
}

export function activeStaffProfiles(rows: AdminStaffProfile[]): AdminStaffProfile[] {
  return rows.filter(row => !row.archivedAt);
}

export function staffProfileMapById(rows: AdminStaffProfile[]): Map<string, AdminStaffProfile> {
  return new Map(rows.map(row => [row.id, row]));
}

export function staffProfileOptionLabel(profile: AdminStaffProfile): string {
  const displayName = profile.displayName.trim();
  const identity = profile.staffIdentity.trim();
  const email = profile.workEmail?.trim();
  const base = displayName && displayName !== identity ? `${displayName} (${identity})` : identity || profile.id;
  return email ? `${base} — ${email}` : base;
}

export function staffWorkEmailLabel(profile?: AdminStaffProfile | null): string | null {
  const email = profile?.workEmail?.trim();
  return email || null;
}

export function staffIdentityFromName(name: string): string {
  const slug = name
    .trim()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return `staff-${slug || 'member'}`;
}

export function staffProfilesListPath(includeArchived: boolean): string {
  return includeArchived ? '/api/admin/staff?includeArchived=1' : '/api/admin/staff';
}

export function staffProfilesReadyForSetupInvite(rows: AdminStaffProfile[]): AdminStaffProfile[] {
  return activeStaffProfiles(rows).filter(canSendStaffSetupInvite);
}

export function canSendStaffSetupInvite(profile: AdminStaffProfile): boolean {
  return !profile.archivedAt && Boolean(staffWorkEmailLabel(profile)) && !profile.userId;
}

export function staffSetupInviteHint(profile?: AdminStaffProfile | null): string {
  const email = staffWorkEmailLabel(profile);
  if (!profile) return 'Choose a Team profile with a work email to send a setup invite.';
  if (profile.archivedAt) return 'Archived Team profiles cannot receive setup invites.';
  if (!email) return 'Add a work email to this Team profile before sending a setup invite.';
  if (profile.userId) return 'This Team profile is already linked to an app member.';
  return `Send setup to ${email}. They should continue with Google using this work email.`;
}

export function staffLabel(input: {
  profile?: AdminStaffProfile | null;
  legacyStaffName?: string | null;
  legacyStaffIdentity?: string | null;
  legacyIdentity?: string | null;
  fallback?: string;
}): string {
  const fallback = input.fallback ?? 'Unassigned';
  const canonicalDisplay = input.profile?.displayName?.trim();
  if (canonicalDisplay) return canonicalDisplay;
  const canonicalIdentity = input.profile?.staffIdentity?.trim();
  if (canonicalIdentity) return canonicalIdentity;
  const legacyName = input.legacyStaffName?.trim();
  if (legacyName) return legacyName;
  const legacyStaffIdentity = input.legacyStaffIdentity?.trim();
  if (legacyStaffIdentity) return legacyStaffIdentity;
  const legacyIdentity = input.legacyIdentity?.trim();
  if (legacyIdentity) return legacyIdentity;
  return fallback;
}

export interface StaffAccessLinkSummary {
  state: 'linked' | 'unlinked';
  label: string;
  detail: string;
}

export function staffAccessLinkSummary(input: {
  userId?: string | null;
  memberLabel?: string | null;
}): StaffAccessLinkSummary {
  const userId = input.userId?.trim();
  if (!userId) {
    return {
      state: 'unlinked',
      label: 'No access member linked',
      detail: 'This Team profile can own numbers/devices without granting anyone app access.',
    };
  }

  const memberLabel = input.memberLabel?.trim();
  return {
    state: 'linked',
    label: memberLabel || `Member ${userId}`,
    detail: memberLabel
      ? 'Linked for identity alignment only; roles and permissions still come from Members.'
      : `Linked to Better Auth user ${userId}; roles and permissions still come from Members.`,
  };
}

export type StaffSetupStatusKey =
  | 'complete'
  | 'mobile_ready'
  | 'google_matched'
  | 'invite_sent'
  | 'ready_to_invite'
  | 'needs_work_email'
  | 'archived';

export interface StaffSetupStatusSummary {
  key: StaffSetupStatusKey;
  label: string;
  detail: string;
  nextAction: string;
}

export function normalizedStaffEmailSet(rows: Array<{ email?: string | null }>): Set<string> {
  return new Set(rows.map(row => row.email?.trim().toLowerCase() ?? '').filter(Boolean));
}

export function pendingInviteEmails(invitations: Array<{ email?: string | null; status?: string | null }>): Set<string> {
  return normalizedStaffEmailSet(invitations.filter(invitation => !invitation.status || invitation.status === 'pending'));
}

export type StaffInviteActivationStatusKey =
  | 'activated_linked'
  | 'member_needs_profile_link'
  | 'pending_invite'
  | 'ready_to_invite'
  | 'needs_work_email'
  | 'archived';

export interface StaffInviteActivationStatus {
  key: StaffInviteActivationStatusKey;
  label: string;
  detail: string;
  nextAction: string;
  tone: 'emerald' | 'blue' | 'amber' | 'slate';
  done: boolean;
}

export function staffInviteActivationStatus(
  profile: AdminStaffProfile,
  input: {
    pendingInviteEmails?: Set<string>;
    memberEmails?: Set<string>;
    invitationEmailEnabled?: boolean;
  } = {},
): StaffInviteActivationStatus {
  const email = staffWorkEmailLabel(profile)?.toLowerCase() ?? null;
  const hasPendingInvite = Boolean(email && input.pendingInviteEmails?.has(email));
  const hasAccessMemberEmail = Boolean(email && input.memberEmails?.has(email));
  const emailDeliveryEnabled = input.invitationEmailEnabled === true;

  if (profile.archivedAt) {
    return {
      key: 'archived',
      label: 'Archived',
      detail: 'Archived Team profiles are excluded from active invite and activation follow-up.',
      nextAction: 'Restore the profile before inviting or linking app access.',
      tone: 'slate',
      done: false,
    };
  }

  if (!email) {
    return {
      key: 'needs_work_email',
      label: 'Needs work email',
      detail: 'No saved work email is available to compare against members or pending invitations.',
      nextAction: 'Add the staff work email before sending a setup invite.',
      tone: 'slate',
      done: false,
    };
  }

  if (profile.userId) {
    return {
      key: 'activated_linked',
      label: 'Activated + linked',
      detail: 'A current Team profile link exists for app access. This confirms access linkage, not mobile pairing or work-data consent.',
      nextAction: 'Review mobile and Google work-data setup separately if needed.',
      tone: 'emerald',
      done: true,
    };
  }

  if (hasAccessMemberEmail) {
    return {
      key: 'member_needs_profile_link',
      label: 'Member found; link profile',
      detail: 'A current access member uses this work email, but the Team profile is not linked yet.',
      nextAction: 'Link the member to this Team profile from the Members list.',
      tone: 'blue',
      done: false,
    };
  }

  if (hasPendingInvite) {
    return {
      key: 'pending_invite',
      label: 'Invite pending',
      detail: emailDeliveryEnabled
        ? 'A pending invitation exists for this work email. Delivery is configured, but acceptance is not confirmed yet.'
        : 'A pending invitation exists for this work email. Email delivery is off, so Oracord cannot confirm staff received an email.',
      nextAction: emailDeliveryEnabled
        ? 'Wait for staff to accept with this work email, then confirm the Team profile link.'
        : 'Configure email delivery before relying on invite email; cancel and recreate only if staff never received this invite.',
      tone: 'amber',
      done: false,
    };
  }

  return {
    key: 'ready_to_invite',
    label: 'Ready to invite',
    detail: emailDeliveryEnabled
      ? 'This profile has a work email and no current member or pending invite match.'
      : 'This profile has a work email, but email delivery is off for automatic invite delivery.',
    nextAction: emailDeliveryEnabled
      ? 'Send the setup invite to this work email.'
      : 'Create the invite only if you have a safe way to get the invite link to staff.',
    tone: emailDeliveryEnabled ? 'amber' : 'slate',
    done: false,
  };
}

export interface StaffGooglePermissionsSummary {
  state: GoogleWorkspacePermissionState;
  label: string;
  detail: string;
  nextAction: string;
  tone: 'slate' | 'emerald' | 'amber';
}

export function staffGooglePermissionsSummary(profile: AdminStaffProfile): StaffGooglePermissionsSummary {
  const permissions = profile.googleWorkspacePermissions ?? {
    state: 'not_connected' as const,
    gmail: false,
    calendar: false,
    connectedAt: null,
    updatedAt: null,
  };

  if (permissions.state === 'needs_admin_config') {
    return {
      state: 'needs_admin_config',
      label: 'Google work data needs admin setup',
      detail: 'Google OAuth is not configured, so Gmail and Calendar permissions cannot be connected yet.',
      nextAction: 'Configure Google OAuth before asking staff for work-data consent.',
      tone: 'slate',
    };
  }

  if (permissions.state === 'needs_staff_authorization') {
    return {
      state: 'needs_staff_authorization',
      label: 'Google work data needs staff authorization',
      detail: 'Google OAuth is configured, but this Team profile has not granted Gmail and Calendar permissions yet.',
      nextAction: 'Ask staff to authorize Google work data permissions when the consent step is available.',
      tone: 'amber',
    };
  }

  if (permissions.state === 'revoked') {
    return {
      state: 'revoked',
      label: 'Google work data access revoked',
      detail: 'Google work data permission was revoked. This status does not prove live token freshness or ingestion.',
      nextAction: 'Ask staff to reconnect Google work data permissions before relying on Gmail or Calendar setup.',
      tone: 'amber',
    };
  }

  if (permissions.state === 'connected' && permissions.gmail && permissions.calendar) {
    return {
      state: 'connected',
      label: 'Google work data connected',
      detail: 'Gmail and Calendar permissions are connected. This is connection status only; no ingestion or processing is implied here.',
      nextAction: 'Keep using this as a permission foundation until actual Gmail/Calendar ingestion is built.',
      tone: 'emerald',
    };
  }

  if (permissions.state === 'needs_reconnect') {
    return {
      state: 'needs_reconnect',
      label: 'Google work data needs reconnect',
      detail: `Connected scopes are incomplete: Gmail ${permissions.gmail ? 'connected' : 'missing'}, Calendar ${permissions.calendar ? 'connected' : 'missing'}.`,
      nextAction: 'Ask staff to reconnect Google work data permissions. Do not treat Gmail or Calendar ingestion as active yet.',
      tone: 'amber',
    };
  }

  return {
    state: 'not_connected',
    label: 'Google work data not connected',
    detail: 'Gmail and Calendar permissions have not been connected for this Team profile.',
    nextAction: 'Ask staff to connect Google work data permissions when the consent step is available.',
    tone: 'slate',
  };
}

export function staffSetupStatusSummary(
  profile: AdminStaffProfile,
  input: { pendingInviteEmails?: Set<string> } = {},
): StaffSetupStatusSummary {
  const email = staffWorkEmailLabel(profile)?.toLowerCase() ?? null;
  const hasPendingInvite = Boolean(email && input.pendingInviteEmails?.has(email));
  const assignedNumbers = profile.usage?.numbers ?? 0;
  const activeMobileSessions = profile.usage?.deviceSessions?.active ?? 0;

  if (profile.archivedAt) {
    return {
      key: 'archived',
      label: 'Archived',
      detail: 'This Team profile is archived and is not part of active staff setup.',
      nextAction: 'Restore the profile before continuing setup.',
    };
  }

  if (!email) {
    return {
      key: 'needs_work_email',
      label: 'Needs work email',
      detail: 'Google Workspace matching needs a saved work email on the Team profile.',
      nextAction: 'Add the staff work email to this Team profile.',
    };
  }

  if (!profile.userId && hasPendingInvite) {
    return {
      key: 'invite_sent',
      label: 'Invite sent',
      detail: 'A pending app invite exists for this work email; no Google match has linked the profile yet.',
      nextAction: 'Wait for staff to accept and continue with Google Workspace.',
    };
  }

  if (!profile.userId) {
    return {
      key: 'ready_to_invite',
      label: 'Ready to invite',
      detail: 'This active Team profile has a work email and is not linked to an access member yet.',
      nextAction: 'Send the setup invite from this Team profile.',
    };
  }

  if (activeMobileSessions > 0) {
    return {
      key: 'complete',
      label: 'Matched + mobile',
      detail: `Google Workspace is matched and ${activeMobileSessions} active mobile access record${activeMobileSessions === 1 ? '' : 's'} ${activeMobileSessions === 1 ? 'is' : 'are'} linked.${assignedNumbers > 0 ? ` ${assignedNumbers} business number${assignedNumbers === 1 ? '' : 's'} assigned.` : ''}`,
      nextAction: assignedNumbers > 0 ? 'Monitor from existing member surfaces.' : 'Assign a business number when one is ready.',
    };
  }

  if (assignedNumbers > 0) {
    return {
      key: 'mobile_ready',
      label: 'Ready for mobile',
      detail: 'Google Workspace is matched and a business number is assigned; no active mobile access is linked yet.',
      nextAction: 'Ask staff to prepare mobile access from their Google-matched setup page.',
    };
  }

  return {
    key: 'google_matched',
    label: 'Google matched',
    detail: 'This Team profile is linked to an access member through Google Workspace matching.',
    nextAction: 'Assign a business number when appropriate, then have staff prepare mobile access.',
  };
}
